import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Tooltip } from 'antd';
import { SpanButton } from 'component/button_components/button.style';
import TableComponent from 'component/table_component/antTable/table';
import { ReactComponent as ViewIcon } from 'assets/images/SVG/icons/view-icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/SVG/icons/delete-icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { AdminPageSizeWrap, AdminPageWrap, AdminTitleWrap } from 'pages/admin_page/style';
import { BackButton, StyledButton, ThemedButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { LeftArrowIcon } from 'assets/icons/AntDeisgn_icons/iconStyle';
import Swal from 'sweetalert2';
import { SweetSweetAlert } from 'component/sweetalert_components';
import TableLoader from 'component/loader_component/tableLoader';
import CreateApi from 'component/modal_components/api_settings/create_api';
import { CopyOutlined, EyeInvisibleOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import ViewApi from 'component/modal_components/api_settings/view_api';
import { deleteRandomApikey, getRandomApikey, getRoutes, updateRoutes } from 'redux/slices/apiSettings';
import { ApiKeyColumnWrap, ApiKeyRoute, ApiKeyRouteField, ApiKeyRouteLabel, ApiKeyText, ApiKeyTextWrap } from './style';
import { refreshToken } from 'redux/slices/authSlice';
import { AntToggleButton } from 'component/form_component/style';
import { FigCaption } from 'pages/license/style';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';

export default function ApiSettings() {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState({});
  const [visible, setVisible] = useState({});
  const [modal, setModal] = useState(false);
  const [recordData, setRecordData] = useState(null);
  const [checkAction, setCheckAction] = useState(false);
  const [routeEdit, setRouteEdit] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiRouteData, setApiRouteData] = useState({});
  const { data, loading, routeData, routeLoading } = useSelector((state) => state.apikeys);

  useEffect(() => {
    dispatch(getRandomApikey());
    dispatch(getRoutes());
  }, []);

  useEffect(() => {
    setApiRouteData(routeData);
  }, [routeData]);

  const handleCopyClick = (id, keyType, key) => {
    navigator.clipboard.writeText(key);
    setCopied((prev) => ({ ...prev, [`${id}-${keyType}`]: true }));
    setTimeout(() => {
      setCopied((prev) => ({ ...prev, [`${id}-${keyType}`]: false }));
    }, 2000);
  };

  const handleVisibilityToggle = (id, keyType) => {
    setVisible((prev) => ({
      ...prev,
      [`${id}-${keyType}`]: !prev[`${id}-${keyType}`],
    }));
  };

  const obfuscateKey = (key) => '•'.repeat(key.length);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      render: (text, record) => (
        <ApiKeyColumnWrap className='af-superadmin-api-settings-apikey-wrap'>
          <ApiKeyTextWrap className='af-superadmin-api-settings-apikey-textwrap'>
            <ApiKeyText className='af-superadmin-api-settings-apikey-text'>
              {visible[`${record.id}-apiKey`] ? text : obfuscateKey(text)}
            </ApiKeyText>
          </ApiKeyTextWrap>
          <Button
            type='text'
            className='af-superadmin-api-settings-apikey-eyeicon-button'
            icon={visible[`${record.id}-apiKey`] ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => handleVisibilityToggle(record.id, 'apiKey')}
            style={{ marginLeft: '8px' }}
          />
          <Button
            type='text'
            className='af-superadmin-api-settings-apikey-copyicon-button'
            icon={copied[`${record.id}-apiKey`] ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => handleCopyClick(record.id, 'apiKey', text)}
            style={{ marginLeft: '4px' }}
          />
        </ApiKeyColumnWrap>
      ),
    },
    {
      title: 'API Secret',
      dataIndex: 'apiSecret',
      render: (text, record) => (
        <ApiKeyColumnWrap className='af-superadmin-api-settings-apisecret-wrap'>
          <ApiKeyTextWrap className='af-superadmin-api-settings-apisecret-textwrap'>
            <ApiKeyText className='af-superadmin-api-settings-apisecret-text'>
              {visible[`${record.id}-apiSecret`] ? text : obfuscateKey(text)}
            </ApiKeyText>
          </ApiKeyTextWrap>
          <Button
            type='text'
            className='af-superadmin-api-settings-apisecret-eyeicon-button'
            icon={visible[`${record.id}-apiSecret`] ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => handleVisibilityToggle(record.id, 'apiSecret')}
            style={{ marginLeft: '8px' }}
          />
          <Button
            type='text'
            className='af-superadmin-api-settings-apisecret-copyicon-button'
            icon={copied[`${record.id}-apiSecret`] ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => handleCopyClick(record.id, 'apiSecret', text)}
            style={{ marginLeft: '4px' }}
          />
        </ApiKeyColumnWrap>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      render: (_, record) => (
        <Space className='af-superadmin-api-settings-space-wrap'>
          <Tooltip title='View' color='#597081' className='af-superadmin-api-settings-view-tooltip'>
            <SpanButton
              className='af-superadmin-api-settings-view-tooltip-button'
              onClick={() => handleViewClick(record.id, record)}
            >
              <ViewIcon className='af-superadmin-api-settings-view-tooltip-icon' />
            </SpanButton>
          </Tooltip>

          <Tooltip title='Delete' color='#597081' className='af-superadmin-api-settings-delete-tooltip'>
            <SpanButton
              className='af-superadmin-api-settings-delete-tooltip-button'
              onClick={() => handleDeleteClick(record)}
            >
              <DeleteIcon className='af-superadmin-api-settings-delete-tooltip-icon' />
            </SpanButton>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setModal(true);
    setCheckAction(false);
    dispatch(refreshToken());
  };
  const handleRoute = async (value) => {
    let updatedRoute = { ...apiRouteData };
    if (value !== 'input') {
      updatedRoute.apiInterface = value;
      updatedRoute.apiRoute = routeData.apiRoute;
    } else {
      updatedRoute.apiRoute = updatedRoute.apiRoute ? updatedRoute.apiRoute : 'restapi';
    }
    try {
      setApiLoading(true);
      const responae = await dispatch(updateRoutes(updatedRoute));
      if (responae.payload && responae.payload.status === 200) {
        await SweetSweetAlert({
          sweetData: `${
            routeData.apiInterface !== updatedRoute.apiInterface
              ? updatedRoute.apiInterface
                ? 'Swagger Enabled Successfully'
                : 'Swagger Disabled Successfully'
              : responae.payload.data
          }`,
          icon: 'success',
          buttonColor: 'green',
        });
        await dispatch(getRoutes());
      } else {
        SweetSweetAlert({ text: responae.payload.data, icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
    } finally {
      setApiLoading(false);
      if (value === 'input') {
        setRouteEdit(false);
      }
    }
  };

  const handleViewClick = (id, record) => {
    if (id === record.id) {
      setRecordData(record);
    }
    setCheckAction(true);
    setModal(true);
    dispatch(refreshToken());
  };

  const handleDeleteClick = async (record) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure you want to delete?',
      showConfirmButton: true,
      confirmButtonColor: 'var(--bg-button-default-color)',
      width: '400px',
      heightAuto: false,
      showCancelButton: true,
      cancelButtonColor: 'green',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        container: 'custom-swal-container',
        icon: 'custom-swal-icon',
        title: 'custom-swal-title',
      },
    });

    if (result.isConfirmed) {
      try {
        const sweetAlert = await dispatch(deleteRandomApikey(record.id));
        if (sweetAlert.payload && sweetAlert.payload.status === 200) {
          SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'success', buttonColor: 'green' });
          dispatch(getRandomApikey());
        } else {
          SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
        }
      } catch (error) {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      }
    }
  };

  return (
    <AdminPageWrap className='af-superadmin-api-settings-wrap'>
      <AdminPageSizeWrap className='af-superadmin-api-settings-content'>
        <BackButton className='af-superadmin-api-settings-backbutton' to='/'>
          <LeftArrowIcon className='af-superadmin-api-settings-leftarrow' /> Back
        </BackButton>
        {apiLoading && routeLoading && <ModalOpenLoader />}
        <AdminTitleWrap className='af-superadmin-api-settings-title-wrap'>
          <h2 className='af-superadmin-api-settings-title-head'>API Settings</h2>
        </AdminTitleWrap>
        <ApiKeyRoute className='af-superadmin-api-settings-api-route-wrap'>
          <ApiKeyRouteField className='af-superadmin-api-settings-api-route-wrap-input-wrap '>
            <ApiKeyRouteLabel className='af-superadmin-api-settings-api-route-wrap-label api-route-input-label'>
              API Route:
            </ApiKeyRouteLabel>
            <Input
              value={apiRouteData.apiRoute}
              onChange={(e) =>
                setApiRouteData({
                  ...apiRouteData,
                  apiRoute: e.target.value,
                })
              }
              disabled={!routeEdit}
              className='af-superadmin-api-settings-api-route-wrap-input'
            />
            {routeEdit ? (
              <ApiKeyRouteField className='af-superadmin-api-settings-api-route-wrap-button'>
                <StyledButton
                  className='af-superadmin-api-settings-api-route-wrap-input-edit-submit'
                  type='submit'
                  onClick={() => handleRoute('input')}
                >
                  Save
                </StyledButton>
                <ThemedButton
                  onClick={() => {
                    setApiRouteData({
                      ...apiRouteData,
                      apiRoute: routeData.apiRoute,
                    });
                    setRouteEdit(false);
                  }}
                  className='af-superadmin-api-settings-api-route-theme-button api-route-button-cancel'
                  htmlType='button'
                >
                  Cancel
                </ThemedButton>
              </ApiKeyRouteField>
            ) : (
              <ThemedButton
                onClick={() => setRouteEdit(true)}
                className='af-superadmin-api-settings-api-route-theme-button api-route-button-edit'
                htmlType='button'
              >
                Edit
              </ThemedButton>
            )}
          </ApiKeyRouteField>
          <ApiKeyRouteField className='af-superadmin-api-settings-api-route-wrap-toggle-wrap'>
            <ApiKeyRouteLabel className='af-superadmin-api-settings-api-route-wrap-label api-route-toggle-label'>
              API Interface:
            </ApiKeyRouteLabel>
            <AntToggleButton
              checked={apiRouteData.apiInterface}
              onChange={(e) => {
                setApiRouteData({
                  ...apiRouteData,
                  apiInterface: e,
                });
                handleRoute(e);
              }}
              className='af-superadmin-api-settings-api-route-toggle-button'
            />
            <FigCaption className='af-superadmin-api-settings-api-route-caption'>
              (http(s)://yourdomain.com/swagger/index.html)
            </FigCaption>
          </ApiKeyRouteField>
        </ApiKeyRoute>
        <AdminTitleWrap className='af-superadmin-api-settings-title-wrap'>
          <h2 className='af-superadmin-api-settings-title-head'>API Keys</h2>
          <div className='af-superadmin-api-settings-title-button-wrap'>
            <StyledButton type='primary' onClick={handleCreate} className='af-superadmin-api-settings-title-button'>
              Create New
            </StyledButton>
          </div>
        </AdminTitleWrap>
        <div className='af-superadmin-api-settings-table-wrap'>
          <TableComponent
            columns={columns}
            data={data}
            loading={{
              spinning: loading,
              indicator: <TableLoader />,
            }}
          />
        </div>
      </AdminPageSizeWrap>
      {modal && (
        <Modal
          className='af-superadmin-api_settings-modal'
          open={modal}
          maskClosable={false}
          footer={[null]}
          onCancel={() => setModal(false)}
          centered
          width={550}
        >
          {checkAction ? <ViewApi recordData={recordData} /> : <CreateApi setModal={setModal} />}
        </Modal>
      )}
    </AdminPageWrap>
  );
}
